import React from 'react';
import { useHistory } from 'react-router-dom';
import { useActiveResume } from '../../context/resume-context';
import { useUser } from '../../hooks/use-user';
import {
  Box,
  Button,
  CrossIcon,
  Flex,
  GiftIcon,
  IconButton,
  Link,
  Logo,
  MenuIcon,
  MenuDrawer,
  PlusIcon,
  Text,
  backgroundTransition,
} from '../../ui';
import ResumesList from './ResumesList';
import { createBlankResume } from 'api/resume';

type Props = {
  isDark: boolean;
};

const MainMenu = ({ isDark }: Props) => {
  return (
    <MenuDrawer OpenButton={<MenuButton isDark={isDark} />}>
      <DrawerContents />
    </MenuDrawer>
  );
};

type MenuButtonProps = {
  isDark: boolean;
  onClick?: () => void;
};

const MenuButton = React.memo<MenuButtonProps>(
  function MenuButton({ isDark, onClick }: MenuButtonProps) {
    return (
      <Button
        variant="link"
        sx={{ width: 'auto', p: 3 }}
        onClick={onClick}
        id="gi-main-menu-button"
      >
        <MenuIcon
          color={isDark ? 'white' : 'black70'}
          size={24}
          sx={{
            transition: `stroke ${backgroundTransition}`,
          }}
        />
        <Text
          variant={isDark ? 'baseDark' : 'base'}
          sx={{
            pl: 2,
            display: ['none', 'none', 'block'],
            transition: `color ${backgroundTransition}`,
            color: isDark ? 'white70' : 'black70',
          }}
        >
          Resumes
        </Text>
      </Button>
    );
  },
  function (prevProps: MenuButtonProps, nextProps: MenuButtonProps): boolean {
    if (prevProps.isDark === nextProps.isDark) return true;
    return false;
  }
);

function DrawerContents({ closeDrawer }: { closeDrawer?: () => void }) {
  // Get the active user and resume
  const user = useUser();
  const resume = useActiveResume();
  const { push } = useHistory();

  if (!resume) return null;

  return (
    <Box
      sx={{
        overflow: 'auto',
        height: '100%',
        px: 3,
        pt: 40,
        pb: 100,
      }}
    >
      <Flex
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          flexDirection: 'row-reverse',
          px: 3,
          py: 2,
          zIndex: 2,
        }}
      >
        <IconButton onClick={closeDrawer}>
          <CrossIcon color="white" />
        </IconButton>
      </Flex>
      {user && (
        <Box>
          <Text variant="listHeadingDark" mb={2}>
            Resumes
          </Text>
          <ResumesList user={user} closeMenu={closeDrawer} />
          <Button
            onClick={() => {
              createBlankResume({ userId: resume.owner }).then((resume) => {
                push(`/resumes/${resume.id}/write`);
                closeDrawer && closeDrawer();
              });
            }}
            variant="link"
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 3,
              py: 2,
              ml: -3,
              color: 'blueDark',
              fontWeight: 'body',
            }}
          >
            <PlusIcon color="blueDark" mr={2} />
            Create new resume
          </Button>
        </Box>
      )}

      <Link
        to="/friends"
        sx={{
          display: 'flex',
          color: 'blueDark',
          fontWeight: 'body',
          pl: '16px',
          py: 2,
          ml: -3,
          mt: 3,
          alignItems: 'center',
        }}
      >
        <GiftIcon
          color="blueDark"
          sx={{
            transition: 'stroke 150ms ease-in-out',
            mr: 3,
          }}
        />
        Invite friends
      </Link>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 3,
        }}
      >
        <Logo color="white" />
      </Box>
    </Box>
  );
}

export default MainMenu;
